import React, { useState, useEffect } from 'react';
import {
  TextField, Button, Select, MenuItem, FormControl, InputLabel,
  TableCell, TableRow, TableBody, TableHead, TableContainer,
  Table, Paper, Typography, Autocomplete, Chip, Box, Alert
} from '@mui/material';
import CommonDialog from '../CommonDialog/CommonDialog';
import { getLibraryByUserId } from '../../services/libraryService';
import { getCellsByLibIdAndViewType, getCellsByLibraryId } from '../../services/cellService';
import { getViewsByViewType, getViewWithPinsById } from '../../services/viewService';
import { VIEW_TYPES } from "../../constants/Constants";

const AddSymbol = ({ open, onClose, onCellSelect, onOpenManageLibrary }) => {
  const userId = localStorage.getItem('user_id');

  const [libraries, setLibraries] = useState([]);
  const [cells, setCells] = useState([]);
  const [views, setViews] = useState([]);

  const [library, setLibrary] = useState('');
  const [cellName, setCellName] = useState('');
  const [view, setView] = useState('');

  const [selectedLibrary, setSelectedLibrary] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);

  const [pins, setPins] = useState([]);
  const [availablePinNames, setAvailablePinNames] = useState([]);
  const [inputValues, setInputValues] = useState({});
  const [viewNameError, setViewNameError] = useState('');
  const [pinTypeMap, setPinTypeMap] = useState({});
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (open) {
      fetchLibraries();
      resetSelections();
    }
  }, [open]);

  useEffect(() => {
    if (selectedLibrary?.library_id) {
      fetchCells(selectedLibrary.library_id);
      setSelectedCell(null);
      setView('');
      setPins([]);
      setAvailablePinNames([]);
      setViewNameError('');
      setErrorMsg('');
    } else {
      setCells([]);
      setSelectedCell(null);
      setView('');
      setPins([]);
      setAvailablePinNames([]);
    }
  }, [selectedLibrary]);

  useEffect(() => {
    if (selectedCell?.cell_id) {
      fetchViews(selectedCell.cell_id);
      setViewNameError('');
      setErrorMsg('');
    } else {
      setView('');
      setPins([]);
      setAvailablePinNames([]);
    }
  }, [selectedCell]);

  const fetchLibraries = async () => {
    try {
      const response = await getLibraryByUserId(userId);
      if(!response.success) return;
    
      const data = response.body;
      setLibraries(data);
    } catch (err) {
      console.error("Error fetching libraries:", err);
    }
  };

  const fetchCells = async (libraryId) => {
    try {
      const response = await getCellsByLibIdAndViewType(libraryId,VIEW_TYPES.schematic);
      if(!response.success) return;
      const data = response.body;
      setCells(data);
    } catch (err) {
      console.error("Error fetching cells:", err);
    }
  };

  const fetchViews = async (cellId) => {
    try {
      
      const response = await getViewsByViewType(cellId, [VIEW_TYPES.symbol, VIEW_TYPES.schematic]);
      if(!response.success) return;
      const viewsByViewType = response.body;

      const symbolViews = viewsByViewType.filter(v => v.view_type_id === VIEW_TYPES.symbol);
      const schematicViews = viewsByViewType.filter(v => v.view_type_id === VIEW_TYPES.schematic);
      setViews(symbolViews);

      const schematicView = schematicViews.find((v) => v.name.trim() ==="schematic");
      if (schematicView?.view_id) {
        setView('symbol');
        

        const response = await getViewWithPinsById(schematicView.view_id);
        if(!response.success) return;
        const viewWithPins = response.body;
        
        

        // Create pin name to type mapping
        const typeMapping = {};
        viewWithPins.pins.forEach(pinGroup => {
          if (pinGroup.pinNames && pinGroup.type) {
            const names = pinGroup.pinNames.split(/[, ]+/).filter(Boolean);
            names.forEach(name => {
              typeMapping[name] = pinGroup.type;
            });
          }
        });
        setPinTypeMap(typeMapping);

        const converted = ['left', 'right', 'top', 'bottom'].map(pos => {
          const pinGroup = viewWithPins.pins.find(p => p.position === pos);
          const pinList = pinGroup?.pinNames
            ?.split(/[, ]+/)
            .filter(Boolean)
            .map(name => ({ name, type: pinGroup.type || '' })) || [];
          return { position: pos, pins: pinList };
        });

        // Flatten all available pin names for validation
        const allNames = viewWithPins.pins
          .map(pg => pg.pinNames?.split(/[, ]+/).filter(Boolean) || [])
          .flat();

        setPins(converted);
        setAvailablePinNames(allNames);
        if (allNames.length === 0) {
          setErrorMsg("Cannot proceed: This cell has no pins.");
        }
      } else {
        setView('symbol');
        setPins([]);
        setAvailablePinNames([]);
      }
    } catch (err) {
      console.error("Error fetching views or pins:", err);
    }
  };

  const resetSelections = () => {
    setLibrary('');
    setCellName('');
    setView('');
    setSelectedLibrary(null);
    setSelectedCell(null);
    setCells([]);
    setPins([]);
    setViewNameError('');
    setErrorMsg('');
  };

    const validateViewName = (name) => {
    const trimmed = name.trim().toLowerCase();

    if (!trimmed) {
      setViewNameError('View name cannot be empty.');
      return false;
    }

    if (!/^symbol.*/.test(trimmed)) {
      setViewNameError('View name must start with "symbol".');
      return false;
    }

    const duplicate = views.find(v => v.name.toLowerCase() === trimmed);

    if (duplicate) {
      setViewNameError('View name already exists in this cell.');
      return false;
    }

    setViewNameError('');
    return true;
  };

  const handleCreate = () => {
     if (!validateViewName(view)) return;

    const payload = {
      library_id: selectedLibrary?.library_id,
      library_name: selectedLibrary?.name,
      cell_id: selectedCell?.cell_id,
      cell_name: selectedCell?.name,
      view_name: view,
      view_type_id: VIEW_TYPES.symbol,
      pins: pins,
    };
    onCellSelect(payload);
    onClose();
  };

  return (
    <CommonDialog open={open} onClose={onClose} title="Create Symbol" hideBackDrop={false}>
      <>
        {/* Library Dropdown */}
        <FormControl fullWidth margin="normal" required>
          <InputLabel>Library/Project Name</InputLabel>
          <Select
            value={library}
            onChange={(e) => {
              const selected = libraries.find(lib => lib.name === e.target.value);
              setLibrary(e.target.value);
              setSelectedLibrary(selected);
              setCellName("");
              setSelectedCell(null);
            }}
            label="Library/Project Name"
          >
          {libraries.length === 0 ? (
            <MenuItem value="" sx={{ fontSize: '12px', cursor: 'default' }}>
              <span style={{ color: "#FF0000" }}>
                No libraries yet! {' '}
                <span 
                  style={{ cursor: 'pointer', color: '#0000ff' }}
                  onClick={(e) => {
                    onClose();
                    onOpenManageLibrary();
                  }}
                >
                  Create
                </span>
              </span>
            </MenuItem>
          ) : (
            libraries.map((lib) => (
              <MenuItem key={lib.library_id} value={lib.name}>
                {lib.name}
              </MenuItem>
            )))}
          </Select>
        </FormControl>

        {/* Cell Dropdown */}
        <FormControl fullWidth margin="normal" disabled={!selectedLibrary} required>
          <InputLabel>Cell Name</InputLabel>
          <Select
            value={cellName}
            onChange={(e) => {
              const selected = cells.find(cell => cell.name === e.target.value);
              setCellName(e.target.value);
              setSelectedCell(selected);
            }}
            label="Cell Name"
          >
            {cells.map((cell) => (
              <MenuItem key={cell.cell_id} value={cell.name}>
                {cell.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* View Name Display */}
        <TextField
          fullWidth
          margin='normal'
          label="View Name"
          value={view}
          onChange={(e) => {
            const newVal = e.target.value;
            setView(newVal);
            validateViewName(newVal);
          }}
          required
          error={!!viewNameError}
          helperText={viewNameError}
          inputProps={{ maxLength: 20 }}
        />

        {/* Pin Table */}
        <Typography sx={{ margin: "10px 0px 10px 2px" }}>Pin Specifications</Typography>
        {/* ✅ Show available pin names */}
        {availablePinNames.length > 0 && (
          <Box sx={{ margin: "0px 0px 10px 2px", display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Typography >Available pins : </Typography>
            {availablePinNames.map((name) => (
              <Chip key={name} label={name} size="small" color="primary" variant="outlined" />
            ))}
          </Box>
        )}
        <TableContainer component={Paper} sx={{ marginBottom: "10px" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Position</strong></TableCell>
                <TableCell><strong>Pin Names</strong></TableCell>
                <TableCell><strong>No of Pins</strong></TableCell>
              </TableRow>
            </TableHead>
          {!errorMsg && (
            <TableBody>
              {pins.map((group) => (
                <TableRow key={group.position}>
                  <TableCell>{group.position}</TableCell>
                  <TableCell>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={group.pins.map(p => p.name)}
                      filterSelectedOptions={false} // allow duplicates
                      inputValue={inputValues[group.position] || ""}
                      onInputChange={(e, newInputValue) => {
                        setInputValues(prev => ({
                          ...prev,
                          [group.position]: newInputValue
                        }));
                      }}
                      onChange={(e, newValue) => {
                        // Ensure only allowed names are kept
                        const filtered = newValue.filter(name => availablePinNames.includes(name));
                        setPins(prev =>
                          prev.map(g =>
                            g.position === group.position
                              ? { ...g, pins: filtered.map(name => ({ name, type: pinTypeMap[name] })) }
                              : g
                          )
                        );
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            label={option}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.75rem',
                              '& .MuiChip-deleteIcon': {
                                fontSize: '1rem'
                              }
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Pins"
                          onKeyDown={(e) => {
                            if (e.key === ' ') {  // use space instead of enter
                              e.preventDefault();
                              const input = e.target.value.trim();
                              if (input && availablePinNames.includes(input)) {
                                setPins(prev =>
                                  prev.map(g =>
                                    g.position === group.position
                                      ? { ...g, pins: [...g.pins, { name: input, type: pinTypeMap[input] }] }
                                      : g
                                  )
                                );
                                // ✅ clear only this row’s input
                                setInputValues(prev => ({ ...prev, [group.position]: "" })); 
                              }
                            }
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": { padding: "8px" }
                          }}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell>{group.pins.length}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
          </Table>
        </TableContainer>
      </>
      <Box className="custom-alert">
        {errorMsg &&
          <Alert severity="warning" sx={{ backgroundColor: 'transparent'}}>
            {errorMsg}
          </Alert>
        }
        <Button variant="contained" className="customButtonStyle" onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          className="customButtonStyle"
          onClick={handleCreate}
          disabled={!selectedLibrary || !selectedCell || !view || !!viewNameError || errorMsg}
        >
          Create
        </Button>
      </Box>
    </CommonDialog>
  );
};

export default AddSymbol;
